import React from 'react'
import { Config } from 'mixpanel-browser'

/**
 * Defines the structure of the Mixpanel context object.
 */
export interface MixpanelContextType {
  /**
   * Tracks an event with optional properties.
   *
   * @param eventName The name of the event.
   * @param properties Optional properties to include with the event.
   */
  track: (eventName: string, properties?: Record<string, any>) => void
  /**
   * Identifies the user with a distinct ID.
   *
   * @param distinctId The unique ID of the user.
   * @param properties Optional properties to associate with the user.
   */
  identify: (distinctId: string, properties?: Record<string, any>) => void
  /**
   * Alias the current user ID with a new ID.
   *
   * @param alias The new alias ID.
   * @param originalId The original ID (optional).
   */
  alias: (alias: string, originalId?: string) => void
  /**
   * Sets properties for the user in Mixpanel People.
   *
   * @param properties An object containing the user properties to set.
   */
  people: {
    set: (properties: Record<string, any>) => void
  }
  /**
   * Resets the Mixpanel identity.
   */
  reset: () => void
  /**
   * Indicates whether the Mixpanel SDK is initialized.
   */
  isInitialized: boolean
  /**
   * Indicates whether the user has been identified.
   */
  hasIdentifiedUser: boolean
  /**
   * Indicates the session is sampled or not.
   */
  isSessionSampled: boolean
}

export const MixpanelContext = React.createContext<MixpanelContextType | undefined>(undefined)

/**
 * Defines the props for the MixpanelProvider component.
 */
export interface MixpanelProviderProps {
  /**
   * The Mixpanel API token.
   */
  token: string
  /**
   * The child components to render.
   */
  children: React.ReactNode
  /**
   * The initial distinct ID to identify the user with.
   */
  initialDistinctId?: string
  /**
   * The sample rate for traces, represented as a number.
   * This is typically a value between 0 and 1, indicating the percentage of traces to sample
   */
  tracesSampleRate: number
  /**
   * Optional Mixpanel configuration object.
   */
  config?: Partial<Config>
}
